import { useCallback, useContext } from 'react';
import { CubeContext } from '@cubejs-client/react';
import dayjs from 'dayjs';
import { FilterType } from '@/models/filter/FilterType';
import { FILTER_CONFIG } from '@/models/filter/filterConfig';
import { FilterState } from '@/types/filter';
import { useAuthContext } from '@/context/AuthContext';
import { Period } from '@/types/date';
import {
  createValidateFilterValues,
  validateGlobalFilters,
  validateLocalFilters,
} from './useSharedViewValidation.helpers';

export enum SharedViewScenario {
  /** Happy path — apply silently */
  APPLY = 'APPLY',
  /** Country/region differs, all filter values valid for recipient */
  REGION_MISMATCH = 'REGION_MISMATCH',
  /** Country/region differs AND some filter values missing for recipient */
  REGION_MISMATCH_WITH_MISSING = 'REGION_MISMATCH_WITH_MISSING',
  /** Same region but some filter values not found in CES */
  VALUES_NOT_FOUND = 'VALUES_NOT_FOUND',
  /** System error or corrupted URL */
  ERROR = 'ERROR',
}

export interface MissingFilterEntry {
  filterKey: string;
  filterLabel: string;
  missingValues: string[];
}

export interface SharedViewValidationResult {
  scenario: SharedViewScenario;
  validFilters: FilterState;
  validLocalFilters?: Record<string, Record<string, unknown>>;
  missingFilters: MissingFilterEntry[];
  senderCountryCodes: string[];
  recipientCountryCodes: string[];
}

const arraysHaveSameElements = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  const sorted1 = [...a].sort();
  const sorted2 = [...b].sort();
  return sorted1.every((code, index) => code === sorted2[index]);
};

interface UseSharedViewValidationResult {
  validateSharedFilters: (
    sharedFilters: FilterState,
    senderCountryCodes: string[],
    localFilters?: Record<string, Record<string, unknown>>,
    resolvedCountryCodes?: string[],
    skipCountryLock?: boolean,
  ) => Promise<SharedViewValidationResult>;
}

const useSharedViewValidation = (): UseSharedViewValidationResult => {
  const { cubejsApi } = useContext(CubeContext);
  const { userInfo } = useAuthContext();

  const validateSharedFilters = useCallback(
    async (
      sharedFilters: FilterState,
      senderCountryCodes: string[],
      localFilters?: Record<string, Record<string, unknown>>,
      resolvedCountryCodes?: string[],
      skipCountryLock?: boolean,
    ): Promise<SharedViewValidationResult> => {
      const recipientCountryCodes = userInfo?.countries ?? [];
      const isRecipientLocal = recipientCountryCodes.length > 0;

      // When the sender is a global user (no country restrictions) but selected
      // specific countries in the filter, use the resolved country codes for
      // the mismatch check. This avoids a false "region mismatch" when a global
      // user shares a Germany-filtered view with a Germany-local user.
      const effectiveSenderCodes =
        senderCountryCodes.length === 0 && resolvedCountryCodes?.length ? resolvedCountryCodes : senderCountryCodes;

      const hasRegionMismatch =
        isRecipientLocal && !arraysHaveSameElements(effectiveSenderCodes, recipientCountryCodes);

      const filtersToValidate = { ...sharedFilters };
      // Local users have a locked country filter — always reset to default
      // so the shared value doesn't appear as an "applied filter".
      if (isRecipientLocal && !skipCountryLock) {
        filtersToValidate[FilterType.COUNTRY] = FILTER_CONFIG[FilterType.COUNTRY].defaultValue;
      }

      const missingFilters: MissingFilterEntry[] = [];
      const validFilters: FilterState = { ...filtersToValidate };
      const validateFilterValues = createValidateFilterValues(cubejsApi, userInfo);

      // Rehydrate dateRange: the raw payload has string dates but
      // filter configs' getOptionsQuery expects dayjs objects.
      const rawDateRange = filtersToValidate[FilterType.DATE_RANGE] as Period | undefined;
      let dateRange: Period | undefined;
      if (rawDateRange?.beginn && rawDateRange?.end) {
        const beginn = typeof rawDateRange.beginn === 'string' ? dayjs(rawDateRange.beginn) : rawDateRange.beginn;
        const end = typeof rawDateRange.end === 'string' ? dayjs(rawDateRange.end) : rawDateRange.end;
        dateRange = beginn.isValid() && end.isValid() ? { beginn, end, period: rawDateRange.period } : undefined;
      }

      await validateGlobalFilters(
        filtersToValidate,
        validFilters,
        dateRange,
        missingFilters,
        validateFilterValues,
        userInfo,
      );

      let validLocalFilters: Record<string, Record<string, unknown>> | undefined;
      if (localFilters) {
        validLocalFilters = await validateLocalFilters(
          localFilters,
          dateRange,
          missingFilters,
          validateFilterValues,
          userInfo,
        );
      }

      let scenario: SharedViewScenario;
      if (hasRegionMismatch && missingFilters.length > 0) {
        scenario = SharedViewScenario.REGION_MISMATCH_WITH_MISSING;
      } else if (hasRegionMismatch) {
        scenario = SharedViewScenario.REGION_MISMATCH;
      } else if (missingFilters.length > 0) {
        scenario = SharedViewScenario.VALUES_NOT_FOUND;
      } else {
        scenario = SharedViewScenario.APPLY;
      }

      return {
        scenario,
        validFilters,
        validLocalFilters,
        missingFilters,
        senderCountryCodes: effectiveSenderCodes,
        recipientCountryCodes,
      };
    },
    [cubejsApi, userInfo],
  );

  return { validateSharedFilters };
};

export default useSharedViewValidation;
